/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ErrorCode } from '../../modules/order-details';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { trackedAction } from '../../utilities/track-execution-time';

/**
 * GetShipTos Input Action
 */
export class GetShipTosInput implements Msdyn365.IActionInput {
    public id: string;
    constructor(id: string) {
        this.id = id;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `GetShipTos-${this.id}`;
    public getCacheObjectType = () => 'GetShipTos';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetShipTosData {
    result: {
        '@odata.context'?: string;
        value?: IShipToTableData[];
    };
    error?: ErrorCode;
}

export interface IShipToTableData {
    Id: string;
    Name1: string;
    Name2: string;
    VatId: string;
    Street1: string;
    HouseNumber: string;
    Street4: string;
    Street5: string;
    PostlCode: string;
    City: string;
    Country: string;
    Phone: string;
    Mail: string;
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate: string;
    SalesAreas: ISalesArea[];
    LocalAddress: ILocalAddress;
    ExtensionProperties: [];
}

export interface ILocalAddress {
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate: string;
    ExtensionProperties: [];
}

export interface ISalesArea {
    SalesOrg: string;
    SalesOrgText: string;
    DistributionChannel: string;
    DistributionChannelText: string;
    Division: string;
    DivisionText: string;
    OrderCreate: boolean;
    RecordCreatedDate: Date;
    ExtensionProperties: [];
}

export const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetShipTosInput('');
};

async function action(input: GetShipTosInput, ctx: Msdyn365.IActionContext): Promise<IGetShipTosData> {
    ensureAuthenticated(ctx);
    const response = await Msdyn365.sendRequest<IGetShipTosData>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetShipTos(language='${ctx.requestContext.locale}',soldTo='${input.id}')`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    );
    if (response.status === 200 && response.data) {
        return { ...response.data };
    } else {
        return { result: { value: [] }, error: { errorCode: response.status, errorText: response.statusText } };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetShipTosData>>((input, ctx) => trackedAction(action, input, ctx, 'GetShipTos')),
    id: 'GetShipTos',
    input: createInput
});
